/**
* Localized HowTo steps for /:locale/services routes.
*
* 5-step notarization + legalization workflow, hand-authored per locale
* to feed HowTo schema.org JSON-LD. Unique wording per language — no
* machine translation, no lorem, no duplication.
*/

export type HowToLocale = "en" | "zh" | "ja" | "ko" | "vi" | "id";

export interface LocalizedHowTo {
  name: string;
  description: string;
  totalTime: string; // ISO 8601
  steps: Array<{ name: string; text: string }>;
}

export const SERVICES_HOWTO: Record<HowToLocale, LocalizedHowTo> = {
  en: {
    name: "How to notarize and legalize documents in Thailand",
    description:
      "The five-step workflow used by Thai Notary Law to notarize, translate, and legalize documents for embassy or overseas use.",
    totalTime: "P5D",
    steps: [
      { name: "Consult and quote", text: "Send scans via LINE or email. We reply within 2 business hours with a fixed quote, destination-specific chain (Apostille or embassy), and turnaround estimate." },
      { name: "Prepare and verify documents", text: "Bring or courier the originals. Our attorney verifies identity, checks signatures, and confirms the document meets the target authority's format." },
      { name: "Notarial certification", text: "The licensed Notarial Services Attorney signs, seals, and records the act in our register under Lawyers Council of Thailand Regulation 2551 B.E." },
      { name: "Certified translation (if required)", text: "Our in-house translators produce the certified translation in the required language. The translation is bound to the notarized original." },
      { name: "MFA and embassy legalization", text: "We submit to the Thai MFA (Chaeng Watthana) and the destination embassy. Documents are couriered back or collected in person." },
    ],
  },
  zh: {
    name: "泰国文件公证与认证五步流程",
    description: "泰国公证律师事务所处理文件公证、翻译与领事认证的标准流程。",
    totalTime: "P5D",
    steps: [
      { name: "咨询与报价", text: "通过 LINE 或邮件发送扫描件,2 个工作小时内回复固定报价与目的国认证链(Apostille 或领事馆)。" },
      { name: "文件准备与核验", text: "递交或快递原件,律师核对身份、签名并确认格式符合目的地要求。" },
      { name: "公证签署", text: "由持牌 Notarial Services Attorney 依据泰国律师公会 2551 B.E. 规范签署、盖章并登记。" },
      { name: "认证翻译(如需)", text: "内部译员出具所需语言的认证翻译,并与公证原件装订。" },
      { name: "外交部与领事馆认证", text: "由团队送交泰国外交部(Chaeng Watthana)及目的国驻泰使领馆,完成后快递或到店领取。" },
    ],
  },
  ja: {
    name: "タイでの公証・領事認証の 5 ステップ",
    description: "Thai Notary Law が公証・翻訳・領事認証を行う標準ワークフロー。",
    totalTime: "P5D",
    steps: [
      { name: "相談と見積もり", text: "LINE またはメールでスキャンを送付。2 営業時間以内に確定見積り、認証チェーン(アポスティーユ/大使館)、納期を回答。" },
      { name: "書類準備と本人確認", text: "原本を持参または郵送。弁護士が本人確認、署名照合、書式適合を確認。" },
      { name: "公証実施", text: "認可 Notarial Services Attorney がタイ弁護士会 2551 B.E. 規則に基づき署名・押印・登録。" },
      { name: "認証翻訳(必要な場合)", text: "社内翻訳者が指定言語で認証翻訳を作成し、公証済み原本と綴じ込み。" },
      { name: "外務省・大使館認証", text: "タイ外務省(Chaeng Watthana)および目的国大使館へ提出。完了後、郵送または来店受取。" },
    ],
  },
  ko: {
    name: "태국 공증 및 영사 인증 5단계",
    description: "타이 노터리 로 사무소의 표준 공증·번역·영사 인증 워크플로.",
    totalTime: "P5D",
    steps: [
      { name: "상담 및 견적", text: "LINE 또는 이메일로 스캔본 전송. 2 영업시간 이내에 확정 견적, 인증 체인(아포스티유/대사관), 소요 기간 회신." },
      { name: "서류 준비 및 확인", text: "원본을 지참하거나 우편으로 발송. 변호사가 신원, 서명, 서식 요건을 검증." },
      { name: "공증 진행", text: "인가 Notarial Services Attorney가 태국변호사협회 2551 B.E. 규정에 따라 서명·날인·등록." },
      { name: "인증 번역(필요 시)", text: "내부 번역가가 지정 언어로 인증 번역을 작성해 공증본과 함께 편철." },
      { name: "외교부·대사관 인증", text: "태국 외교부(Chaeng Watthana) 및 목적국 대사관에 접수. 완료 후 배송 또는 방문 수령." },
    ],
  },
  vi: {
    name: "5 bước công chứng và hợp pháp hóa tại Thái Lan",
    description: "Quy trình chuẩn của Văn phòng Luật Thai Notary để công chứng, dịch và hợp pháp hóa lãnh sự.",
    totalTime: "P5D",
    steps: [
      { name: "Tư vấn và báo giá", text: "Gửi bản scan qua LINE hoặc email. Phản hồi trong 2 giờ làm việc kèm báo giá cố định, chuỗi hợp pháp hóa (Apostille/đại sứ quán) và thời gian." },
      { name: "Chuẩn bị và xác minh hồ sơ", text: "Mang trực tiếp hoặc chuyển phát bản gốc. Luật sư xác minh danh tính, chữ ký và định dạng theo yêu cầu." },
      { name: "Chứng thực công chứng", text: "Notarial Services Attorney được cấp phép ký, đóng dấu và ghi sổ theo Quy chế 2551 B.E. của Liên đoàn Luật sư Thái Lan." },
      { name: "Dịch công chứng (nếu cần)", text: "Đội ngũ dịch nội bộ thực hiện bản dịch công chứng bằng ngôn ngữ yêu cầu và đóng kèm bản gốc." },
      { name: "Hợp pháp hóa MFA và đại sứ quán", text: "Nộp cho Bộ Ngoại giao Thái Lan (Chaeng Watthana) và đại sứ quán đích. Hoàn tất bằng chuyển phát hoặc nhận tại văn phòng." },
    ],
  },
  id: {
    name: "5 langkah notarisasi dan legalisasi di Thailand",
    description: "Alur kerja standar Thai Notary Law untuk notarisasi, terjemahan tersumpah, dan legalisasi kedutaan.",
    totalTime: "P5D",
    steps: [
      { name: "Konsultasi dan penawaran", text: "Kirim pindaian via LINE atau email. Balasan dalam 2 jam kerja berisi penawaran tetap, rantai legalisasi (Apostille/kedutaan), dan estimasi waktu." },
      { name: "Persiapan dan verifikasi dokumen", text: "Antar langsung atau kirim dokumen asli. Pengacara memverifikasi identitas, tanda tangan, dan format sesuai otoritas tujuan." },
      { name: "Pengesahan notaris", text: "Notarial Services Attorney berlisensi menandatangani, memberi stempel, dan mencatat berdasarkan Regulasi 2551 B.E. Lawyers Council of Thailand." },
      { name: "Terjemahan tersumpah (jika perlu)", text: "Penerjemah internal menyiapkan terjemahan tersumpah dalam bahasa yang diperlukan dan menyatukannya dengan dokumen asli." },
      { name: "Legalisasi MFA dan kedutaan", text: "Diajukan ke Kementerian Luar Negeri Thailand (Chaeng Watthana) dan kedutaan tujuan. Selesai dengan kurir atau pengambilan langsung." },
    ],
  },
};
